/**
 * <carousel-slider>: a native overflow-scroll + CSS scroll-snap rail with a
 * conveyor-belt infinite loop.
 *
 * We use native scroll rather than a JS transform rail because iOS Safari runs
 * overflow scrolling on the compositor, so touch drags stay smooth while images
 * decode and the layout reflows. A transform rail can't match that: calling
 * preventDefault() on pointermove, or having a ResizeObserver re-apply the
 * transform mid-gesture when a lazy image decodes, both cause microjumps back
 * to the pre-drag origin. With native scroll the browser owns the scroll
 * position and we never fight it.
 *
 * The conveyor-belt rebalance runs only on scrollend, between gestures, so we
 * never mutate the DOM while a scroll is in flight. Safari < 18.2 gets a
 * debounced-scroll polyfill for scrollend.
 *
 * Expected markup (see snippets/carousel.liquid):
 *
 *   <carousel-slider arrows aria-label="…">
 *     <div data-js="carousel-track">
 *       <article>…</article>
 *       <article>…</article>
 *     </div>
 *   </carousel-slider>
 *
 * Public API: el.next(), el.prev(), el.goTo(index), el.pause(), el.resume(), and el.forceInit()
 * to initialise immediately (used by the tab switcher when a hidden panel
 * becomes visible).
 *
 * Events (bubble + composed): carousel:initialised, carousel:empty, carousel:slide-change.
 */
(function () {
  if (window.customElements.get("carousel-slider")) return;

  /** Minimum horizontal pointer travel (px) for a desktop drag-to-scroll to
   *  count as a drag. Below this we treat it as a tap so clicks pass through to
   *  nested buttons and links. */
  const DRAG_THRESHOLD = 6;

  /** Hard cap on how many slides a single drag-release can traverse. Caps both
   *  the in-drag rubberband travel and the post-release snap target; without
   *  it, a hard flick at the conveyor buffer edge could animate through many
   *  slides at once. */
  const MAX_RELEASE_SLIDES = 3;

  /** Pointer velocity (px/ms in the scroll direction) at release
   *  above which we add a +1 slide "flick bonus" to the snap target.
   *  Tuned so a deliberate flick triggers it but a slow drop does not. */
  const FLICK_VELOCITY = 0.5;

  /** Default snap animation duration (ms) for arrow nav. For
   *  drag-release the duration is derived per-slide so a multi-slide
   *  glide stays paced rather than whipping past in the same time as
   *  a single-slide step. */
  const SNAP_DURATION_MS = 650;

  /** Min/max bounds on release animation duration. */
  const RELEASE_DURATION_MIN = 700;
  const RELEASE_DURATION_MAX = 1500;

  /** Per-slide pacing target (ms). Higher = a 3-slide release glides
   *  more visibly instead of whipping past. Tuned by feel against iOS
   *  native momentum. */
  const RELEASE_MS_PER_SLIDE = 450;

  /** Gentle deceleration curve, easeOutCubic = 1 - (1-t)^3. It decelerates
   *  clearly but enters much more softly than easeOutQuint, which front-loads
   *  ~67% of the travel into the first 20% of time and reads as a "whip then
   *  creep" on desktop. Cubic closely matches iOS native momentum scroll. */
  const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

  /** Idle window (ms) used by the `scrollend` polyfill on browsers
   *  that don't fire the event natively. */
  const SCROLLEND_IDLE_MS = 120;

  const REDUCED_MOTION = window.matchMedia("(prefers-reduced-motion: reduce)");
  const HAS_NATIVE_SCROLLEND = "onscrollend" in window;

  /**
   * Minimal trailing debounce.
   * @template {(...args: any[]) => void} F
   * @param {F} fn
   * @param {number} wait
   * @returns {F}
   */
  const debounce = (fn, wait) => {
    let t;
    return /** @type {F} */ (
      (...args) => {
        clearTimeout(t);
        t = setTimeout(() => fn.apply(null, args), wait);
      }
    );
  };

  class Carousel extends HTMLElement {
    constructor() {
      super();
      this._initialised = false;
      this._destroyed = false;
      /** Logical index in current DOM order. May briefly leave [0,n)
       *  during a step; conveyor belt restores it on scrollend. */
      this._index = 0;
      /** Set by `_evaluateDisabled()` when the visible window can fit every
       *  slide. Nothing to scroll, so arrows are hidden and overflow is
       *  clamped. */
      this._disabled = false;
      /** True while we're applying a no-animation scroll correction
       *  during the conveyor-belt rebalance. Suppresses the second
       *  `scrollend` that the corrective scroll would otherwise emit. */
      this._rebalancing = false;
      /** Desktop click+drag-to-scroll state. Touch input always uses native
       *  scroll, so these fields are only set for mouse/pen. */
      this._isDragging = false;
      this._dragStartX = 0;
      this._dragStartScrollLeft = 0;
      this._dragPointerId = null;
      this._dragMoved = false;
      /** Nested <media-slider> whose hover-image overlay we pin open for the
       *  duration of a desktop drag — pointer capture drops its :hover. */
      this._hoverHoldEl = null;
      /** EMA-smoothed pointer velocity (px/ms, +ve = finger right). */
      this._dragVelocity = 0;
      this._dragLastX = 0;
      this._dragLastT = 0;
      /** Latest target scrollLeft from pointermove. Drained once per
       *  frame by `_dragRafTick` so high-frequency mouse events
       *  (gaming mice fire >> 60Hz) don't pile up scroll writes. */
      this._dragTargetScrollLeft = 0;
      /** rAF handle for the active drag-tracking loop. */
      this._dragRaf = 0;
      /** rAF handle for the in-flight custom snap animation. */
      this._scrollAnimRaf = 0;
      /** True for the duration of the click event that follows a drag. */
      this._suppressNextClick = false;
      this._listeners = [];
      this._pendingInitRaf = 0;
      this._pausedVideos = [];
    }

    connectedCallback() {
      if (this._initialised || this._destroyed) return;

      this._track = this.querySelector(':scope > [data-js="carousel-track"]');
      // The Searchspring subclass mounts an empty track and hydrates
      // asynchronously, so bail and let it call forceInit() once results arrive.
      if (!this._track || this._track.children.length === 0) return;

      if (this.hasAttribute("eager")) {
        this._tryInit();
      } else {
        this._io = new IntersectionObserver(
          (entries) => {
            if (entries.some((e) => e.isIntersecting)) {
              this._io.disconnect();
              this._io = null;
              this._tryInit();
            }
          },
          { rootMargin: "200px" },
        );
        this._io.observe(this);
      }
    }

    disconnectedCallback() {
      // Defer real teardown by a microtask so a synchronous
      // re-attach (e.g. the theme editor moving the section) is a
      // no-op rather than a destroy.
      queueMicrotask(() => {
        if (this.isConnected) return;
        this._destroy();
      });
    }

    /**
     * Ensure this carousel is initialised right now. Called by the tab switcher
     * when a hidden panel becomes visible: IntersectionObserver doesn't fire for
     * display:none subtrees, and a host that mounted with clientWidth === 0
     * deferred its real init.
     */
    forceInit() {
      if (this._destroyed) return;
      if (!this._track) {
        this._track = this.querySelector(':scope > [data-js="carousel-track"]');
      }
      if (!this._track || this._track.children.length === 0) return;
      this._tryInit();
    }

    next() {
      if (!this._initialised || this._isPaused || this._disabled) return;
      this._scrollByStride(1);
    }

    prev() {
      if (!this._initialised || this._isPaused || this._disabled) return;
      this._scrollByStride(-1);
    }

    /**
     * Scroll to the slide whose original (markup-order) index is `index`,
     * using the same eased animation as arrow nav.
     * @param {number} index Zero-based original index.
     * @returns {void}
     */
    goTo(index) {
      if (!this._initialised || this._isPaused || this._disabled) return;
      if (!this._slideStride) return;
      const children = Array.from(this._track.children);
      const target = children.findIndex(
        (s) => Number(s.dataset.freshOriginalIndex) === index,
      );
      if (target === -1) return;
      const delta = target - this._normalisedIndex();
      if (delta === 0) return;
      this._scrollByStride(delta);
    }

    pause() {
      this._isPaused = true;
      this._pausedVideos = [];
      this._track?.querySelectorAll("video").forEach((v) => {
        if (!v.paused) {
          this._pausedVideos.push(v);
          v.pause();
        }
      });
    }

    resume() {
      this._isPaused = false;
      (this._pausedVideos || []).forEach((v) => {
        v.play().catch(() => {
          /* autoplay policy may refuse; fail silently */
        });
      });
      this._pausedVideos = [];
    }

    // Init

    /**
     * Defer init until the host has non-zero box dimensions. A
     * carousel inside an inactive tab mounts with `clientWidth === 0`
     * and would measure garbage offsets.
     */
    _tryInit() {
      if (this._initialised) return;
      if (this.clientWidth === 0) {
        const start = performance.now();
        const wait = () => {
          if (this._destroyed || this._initialised) return;
          if (this.clientWidth > 0) {
            this._init();
          } else if (performance.now() - start < 2000) {
            this._pendingInitRaf = requestAnimationFrame(wait);
          }
        };
        this._pendingInitRaf = requestAnimationFrame(wait);
        return;
      }
      this._init();
    }

    _init() {
      if (this._initialised) return;
      this._initialised = true;

      this._slides = Array.from(this._track.children);
      // Logical total excludes [data-clone="true"] children. Some sections
      // (e.g. instagram-grid on mobile) duplicate their tiles in markup so the
      // centered carousel keeps a symmetric conveyor-belt buffer; those clones
      // must not appear in the slide counter, live-region announcement, or
      // per-slide aria-label numbering.
      this._logicalTotal =
        this._slides.filter((s) => s.dataset.clone !== "true").length ||
        this._slides.length;
      this._i18n = {
        prev: this.dataset.i18nPrev || "Previous slide",
        next: this.dataset.i18nNext || "Next slide",
        announce:
          this.dataset.i18nAnnounce || "Slide {{ index }} of {{ total }}",
        roledescription: this.dataset.i18nRoledescription || "carousel",
        slideRoledescription: this.dataset.i18nSlideRoledescription || "slide",
      };

      // ARIA on host + slides.
      if (!this.hasAttribute("role")) this.setAttribute("role", "region");
      if (!this.hasAttribute("aria-roledescription")) {
        this.setAttribute("aria-roledescription", this._i18n.roledescription);
      }
      let logicalIdx = 0;
      let cloneIdx = 0;
      this._slides.forEach((slide) => {
        if (slide.dataset.clone === "true") {
          // Clones inherit their counterpart's original index so the live
          // region announces the right position when a clone is the
          // front-of-viewport slide. Clones are aria-hidden in markup, so we
          // don't tag them with role/aria-roledescription/aria-label.
          slide.dataset.freshOriginalIndex = String(
            cloneIdx % this._logicalTotal,
          );
          cloneIdx += 1;
          return;
        }
        slide.setAttribute("role", "group");
        slide.setAttribute("aria-roledescription", this._i18n.slideRoledescription);
        slide.setAttribute(
          "aria-label",
          this._format(this._i18n.announce, {
            index: logicalIdx + 1,
            total: this._logicalTotal,
          }),
        );
        // Stable identity tag, used by the live region so we can
        // announce the real product position even after the
        // conveyor belt has rotated the DOM.
        slide.dataset.freshOriginalIndex = String(logicalIdx);
        logicalIdx += 1;
      });

      if (!this._track.id) {
        this._track.id = `carousel-track-${Math.floor(Math.random() * 1e9)}`;
      }
      this._setupArrows();
      this._setupLiveRegion();
      this._setupCount();

      // Keyboard path: the scrollbar is hidden, so make the track focusable
      // and drive it with arrow keys. Skipped in the disabled state (nothing
      // to scroll).
      this._track.setAttribute("tabindex", "0");
      this._track.setAttribute(
        "aria-label",
        this.getAttribute("aria-label") || "",
      );

      this._measure();
      this._setupLazyLoading();
      this._evaluateDisabled({ initial: true });
      this._syncActiveSlide();
      this._bindEvents();

      this.dataset.initialised = "true";
      this.dispatchEvent(
        new CustomEvent("carousel:initialised", {
          bubbles: true,
          composed: true,
        }),
      );
    }

    _destroy() {
      if (this._destroyed) return;
      this._destroyed = true;
      this._io?.disconnect();
      this._lazyIo?.disconnect();
      this._resizeObserver?.disconnect();
      cancelAnimationFrame(this._pendingInitRaf);
      cancelAnimationFrame(this._dragRaf);
      cancelAnimationFrame(this._scrollAnimRaf);
      delete this.dataset.animating;
      if (this._hoverHoldEl) {
        delete this._hoverHoldEl.dataset.hoverHold;
        this._hoverHoldEl = null;
      }
      this._listeners.forEach(({ target, type, fn, opts }) => {
        target.removeEventListener(type, fn, opts);
      });
      this._listeners.length = 0;
    }

    _on(target, type, fn, opts) {
      target.addEventListener(type, fn, opts);
      this._listeners.push({ target, type, fn, opts });
    }

    _format(template, values) {
      return template.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, key) =>
        values[key] != null ? String(values[key]) : "",
      );
    }

    // Setup

    _setupArrows() {
      if (!this.hasAttribute("arrows")) return;

      this._prevButton = this._buildArrow("prev");
      this._nextButton = this._buildArrow("next");
      this.appendChild(this._prevButton);
      this.appendChild(this._nextButton);
    }

    /**
     * @param {'prev'|'next'} dir
     * @returns {HTMLButtonElement}
     */
    _buildArrow(dir) {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = `carousel__arrow carousel__arrow--${dir}`;
      btn.dataset.js = `carousel-arrow-${dir}`;
      btn.setAttribute(
        "aria-label",
        dir === "prev" ? this._i18n.prev : this._i18n.next,
      );
      btn.setAttribute("aria-controls", this._track.id);
      const tpl = this.querySelector(
        `:scope > template[data-js="carousel-icon-${dir}"]`,
      );
      if (tpl) {
        btn.appendChild(tpl.content.cloneNode(true));
      }
      return btn;
    }

    _setupLiveRegion() {
      this._liveRegion = document.createElement("span");
      this._liveRegion.className = "carousel__live";
      this._liveRegion.setAttribute("aria-live", "polite");
      this._liveRegion.setAttribute("aria-atomic", "true");
      this.appendChild(this._liveRegion);
      this._announce = debounce((index) => {
        if (!this._liveRegion) return;
        this._liveRegion.textContent = this._format(this._i18n.announce, {
          index: index + 1,
          total: this._logicalTotal,
        });
      }, 200);
    }

    /**
     * Wire up the optional "1/4" slide counter rendered by the snippet (or by
     * the section, for <athos-carousel> and the inline instagram-grid
     * markup). Direct-child query only, so we never update a nested
     * <media-slider>'s counter from the parent rail.
     */
    _setupCount() {
      this._countEl = this.querySelector(':scope > [data-js="carousel-count"]');
      if (!this._countEl) return;
      this._countCurrentEl = this._countEl.querySelector(
        '[data-js="carousel-count-current"]',
      );
      this._countTotalEl = this._countEl.querySelector(
        '[data-js="carousel-count-total"]',
      );
      this._updateCount();
    }

    _updateCount() {
      if (!this._countEl) return;
      const total = this._logicalTotal || this._slides.length;
      const current = this._currentOriginalIndex() + 1;
      if (this._countTotalEl) this._countTotalEl.textContent = String(total);
      if (this._countCurrentEl) {
        this._countCurrentEl.textContent = String(current);
      }
    }

    _measure() {
      // Cache slide width + gap once so scroll-tick math is cheap.
      const first = this._slides[0];
      if (!first) return;
      const trackStyle = getComputedStyle(this._track);
      const gap =
        parseFloat(trackStyle.columnGap || trackStyle.gap || "0") || 0;
      this._slideWidth = first.getBoundingClientRect().width;
      this._slideStride = this._slideWidth + gap;
      this._trackPeek =
        parseFloat(getComputedStyle(this._track).paddingLeft) || 0;
    }

    /**
     * Are there fewer slides than fit in the visible window? If so the carousel
     * has nothing to scroll, so we render a static rail with no arrows, no
     * scroll, and no infinite-loop bookkeeping.
     *
     * The threshold uses --slides-to-show from CSS (read via computed style on
     * the host), the source of truth for how many cards fit in the viewport at
     * the current breakpoint.
     */
    _isUnderfilled() {
      const slidesToShow = parseFloat(
        getComputedStyle(this).getPropertyValue("--slides-to-show"),
      );
      if (!Number.isFinite(slidesToShow) || slidesToShow <= 0) return false;
      // Need at least one whole extra slide beyond what fits for looping to be
      // meaningful; Math.ceil covers fractional layouts like
      // --slides-to-show: 4.5.
      return this._slides.length <= Math.ceil(slidesToShow);
    }

    /**
     * Apply (or roll back) the underfilled state. Called on init and
     * on every resize.
     */
    _evaluateDisabled({ initial = false } = {}) {
      const shouldDisable = this._isUnderfilled();
      if (!initial && shouldDisable === this._disabled) return;
      this._disabled = shouldDisable;
      if (shouldDisable) {
        this.dataset.disabled = "true";
        this._index = 0;
        // CSS pins overflow to hidden in the disabled state; also reset any
        // prior scroll position so the rail starts at slide 0.
        if (this._track) {
          this._track.scrollLeft = 0;
          this._track.removeAttribute("tabindex");
        }
      } else {
        delete this.dataset.disabled;
        if (this._track) this._track.setAttribute("tabindex", "0");
        // Recenter so we have buffer slides on both sides for the
        // first nav. Safe to call repeatedly.
        this._recenter({ initial: true });
        this._warmDecodeCache();
      }
    }

    /**
     * IntersectionObserver-driven lazy loader for the first card images.
     *
     * We use a custom observer rather than loading="lazy" because native
     * lazy-load uses the layout bounding rect, which ignores ancestor
     * overflow: hidden. Cards positioned far to the right of the visible track
     * (after recentering) still count as "in the viewport", so the browser
     * fetches every product image up front. Rooting our own observer on the
     * track (which clips overflow) means only cards whose rendered position
     * actually intersects the visible region get loaded.
     *
     * Once a card has been swapped in we unobserve it; lazy load is a one-shot
     * operation per card.
     */
    _setupLazyLoading() {
      if (typeof IntersectionObserver === "undefined") {
        // Fallback: load everything synchronously.
        this._track
          .querySelectorAll("img[data-src], video[data-src]")
          .forEach((el) => {
            this._swapDataSrc(el);
          });
        return;
      }
      this._lazyIo = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            const slide = entry.target;
            this._lazyIo.unobserve(slide);
            // If the card hosts a <media-slider>, ask it to load its currently
            // visible slide. We can't just grab the first <img data-src> here:
            // the inner slider rotates its DOM on init, so the visible slide is
            // no longer first in document order.
            const innerSlider = slide.querySelector("media-slider");
            if (innerSlider && typeof innerSlider.loadActive === "function") {
              innerSlider.loadActive();
              return;
            }
            // Plain card with no inner slider: load the first lazy media
            // directly.
            const firstLazy = slide.querySelector(
              "img[data-src], video[data-src]",
            );
            if (firstLazy) this._swapDataSrc(firstLazy);
          });
        },
        // Root on the track itself so the observer respects its
        // overflow clip. Generous rootMargin so adjacent off-screen
        // cards start fetching slightly before they scroll into view.
        { root: this._track, rootMargin: "200px 200px" },
      );
      Array.from(this._track.children).forEach((slide) => {
        this._lazyIo.observe(slide);
      });
    }

    /**
     * Swap an img or video data-src over to a real src. Removes the data
     * attribute so it isn't picked up again, and (for images) queues a decode
     * once the fetch completes so the next paint isn't blocked on decode.
     * @param {HTMLImageElement|HTMLVideoElement} el
     */
    _swapDataSrc(el) {
      const src = el.dataset.src;
      if (!src) return;
      const srcset = el.dataset.srcset;
      if (srcset) {
        el.srcset = srcset;
        delete el.dataset.srcset;
      }
      el.src = src;
      delete el.dataset.src;

      if (el.tagName === "VIDEO") {
        const poster = el.dataset.poster;
        if (poster) {
          el.poster = poster;
          delete el.dataset.poster;
        }
        el.load();
        return;
      }

      const decode = () => {
        if (typeof el.decode === "function") el.decode().catch(() => {});
      };
      if (el.complete && el.naturalWidth > 0) {
        decode();
      } else {
        el.addEventListener("load", decode, { once: true });
      }
    }

    _bindEvents() {
      // Native scroll → track index + drive scrollend rebalance.
      this._on(this._track, "keydown", (e) => {
        if (this._disabled) return;
        if (e.key === "ArrowRight") {
          e.preventDefault();
          this.next();
        } else if (e.key === "ArrowLeft") {
          e.preventDefault();
          this.prev();
        }
      });
      this._on(this._track, "scroll", this._onScroll.bind(this), {
        passive: true,
      });
      if (HAS_NATIVE_SCROLLEND) {
        this._on(this._track, "scrollend", this._onScrollEnd.bind(this));
      }
      // Always run the polyfill alongside. On browsers that do dispatch
      // scrollend, the debounced fallback collapses to a no-op because the
      // native event already cleared our pending state. Cheap insurance for
      // Safari < 18.2 and any UA where scrollend lands inconsistently (e.g. a
      // mid-momentum pointerdown).
      this._scrollEndPoly = debounce(
        () => this._onScrollEnd(),
        SCROLLEND_IDLE_MS,
      );

      // Desktop click+drag-to-scroll. Touch input is left to the browser so it
      // stays on the compositor; only mouse and pen get our manual scrollLeft
      // handling.
      this._on(this._track, "pointerdown", this._onPointerDown.bind(this));
      this._on(this._track, "pointermove", this._onPointerMove.bind(this));
      this._on(this._track, "pointerup", this._onPointerUp.bind(this));
      this._on(this._track, "pointercancel", this._onPointerUp.bind(this));

      // Suppress native image/anchor drag on desktop so a horizontal
      // drag-pan moves the slider, not the dragged image preview.
      this._on(this._track, "dragstart", (e) => {
        if (this._isDragging) e.preventDefault();
      });

      // Capture-phase click suppressor: after a desktop drag, swallow
      // the click that the browser dispatches on pointerup so a
      // dragged product card link doesn't navigate.
      this._on(
        this,
        "click",
        (e) => {
          if (this._suppressNextClick) {
            e.preventDefault();
            e.stopPropagation();
            this._suppressNextClick = false;
          }
        },
        true,
      );

      if (this._prevButton) {
        this._on(this._prevButton, "click", () => this.prev());
      }
      if (this._nextButton) {
        this._on(this._nextButton, "click", () => this.next());
      }

      this._on(
        window,
        "resize",
        debounce(() => {
          this._measure();
          this._evaluateDisabled();
          if (!this._disabled) {
            // Re-snap to the current logical index without animation.
            this._snapToIndex(this._index);
          }
        }, 200),
      );

      if (typeof ResizeObserver !== "undefined") {
        this._resizeObserver = new ResizeObserver(() => {
          const prevStride = this._slideStride;
          this._measure();
          // Only re-snap if the stride actually changed (e.g. a breakpoint
          // layout change). A pure height growth from a lazy image decoding
          // must NOT touch scrollLeft; doing so during a touch drag caused the
          // mid-gesture microjumps in the previous transform-based version.
          if (
            !this._disabled &&
            prevStride &&
            Math.abs(prevStride - this._slideStride) > 0.5
          ) {
            this._snapToIndex(this._index);
          }
        });
        this._resizeObserver.observe(this._track);
      }
    }

    // Scroll handling

    _onScroll() {
      if (this._rebalancing) return;
      // Update derived index from scrollLeft for live-region and counter
      // updates; the authoritative value is still computed from scrollLeft on
      // demand.
      const idx = this._indexFromScroll();
      if (idx !== this._index) {
        this._index = idx;
        if (this._announce) this._announce(this._currentOriginalIndex());
        this._updateCount();
        this._syncActiveSlide();
        this.dispatchEvent(
          new CustomEvent("carousel:slide-change", {
            bubbles: true,
            composed: true,
            detail: {
              index: this._currentOriginalIndex(),
              total: this._logicalTotal,
            },
          }),
        );
      }
      // Fallback scrollend timer in case the native event doesn't
      // fire (Safari < 18.2 or interrupted-momentum edge cases).
      // Suppressed during a desktop drag so the conveyor-belt
      // rebalance can't run mid-gesture and invalidate
      // `_dragStartScrollLeft`, which would cause the next tiny
      // pointermove to map to a huge scroll delta.
      if (this._isDragging) return;
      // Also suppressed during a JS-driven release/arrow animation. The cubic
      // ease-out has an asymptotic tail where per-frame scrollLeft deltas drop
      // below 1px; browsers stop firing scroll events there, so the polyfill's
      // 120ms idle timer would fire before the animation finishes and trigger a
      // _recenter that rotates the DOM mid-animation. The next frame would then
      // write scrollLeft in the now-stale coordinate frame, jumping several
      // slides at once: the "fast loop on release" bug.
      if (this.dataset.animating === "true") return;
      this._scrollEndPoly?.();
    }

    _onScrollEnd() {
      if (this._rebalancing) return;
      // Same guards as _onScroll: native scrollend shouldn't fire mid-drag or
      // mid-animation, but Safari has historically been inconsistent here, and
      // the spec lets UAs fire it during the asymptotic tail of a smooth
      // scroll.
      if (this._isDragging) return;
      if (this.dataset.animating === "true") return;
      if (!this._slideStride || this._disabled) return;
      // Recenter the DOM so the next nav has buffer slides on both
      // sides. Runs only between gestures, so we never mutate the
      // DOM while a scroll is in flight.
      this._index = this._indexFromScroll();
      this._recenter();
    }

    /** Logical slide index derived from current scrollLeft. */
    _indexFromScroll() {
      if (!this._slideStride) return 0;
      return Math.round(this._track.scrollLeft / this._slideStride);
    }

    /**
     * Scroll one stride in the given direction with a smooth
     * cubic-bezier ease-out. Replaces native `behavior: 'smooth'`
     * which is too short and too linear to feel iOS-native.
     * @param {number} dir
     */
    _scrollByStride(dir) {
      if (!this._slideStride) return;
      // Snap the current position to the grid before adding a stride so rapid
      // arrow clicks land cleanly.
      const currentIdx = Math.round(this._track.scrollLeft / this._slideStride);
      const targetIdx = currentIdx + dir;

      const targetChild = this._track.children[targetIdx];
      let targetLeft = targetIdx * this._slideStride;

      if (targetChild) {
        const peek = this._trackPeek || 0;
        if (
          this.dataset.mobileLayout === "centered" &&
          window.innerWidth < 768
        ) {
          targetLeft =
            targetChild.offsetLeft +
            targetChild.offsetWidth / 2 -
            this._track.clientWidth / 2;
        } else {
          targetLeft = targetChild.offsetLeft - peek;
        }
      }

      this._animateScrollTo(targetLeft);
    }

    /**
     * Snap the rail to a given logical index without animation. Used by the
     * recenter and resize handlers, which must be visually instantaneous.
     * @param {number} index
     */
    _snapToIndex(index) {
      if (!this._slideStride) return;
      this._rebalancing = true;

      let targetLeft = index * this._slideStride;
      const targetChild = this._track.children[index];
      if (targetChild) {
        const peek = this._trackPeek || 0;
        if (
          this.dataset.mobileLayout === "centered" &&
          window.innerWidth < 768
        ) {
          targetLeft =
            targetChild.offsetLeft +
            targetChild.offsetWidth / 2 -
            this._track.clientWidth / 2;
        } else {
          targetLeft = targetChild.offsetLeft - peek;
        }
      }

      this._track.scrollTo({
        left: targetLeft,
        behavior: "auto",
      });
      // Release the rebalancing flag on the next frame so any
      // synchronous scroll event from the corrective scroll is
      // suppressed, but a fresh user gesture immediately after is
      // still observed.
      requestAnimationFrame(() => {
        this._rebalancing = false;
      });
    }

    /**
     * rAF-driven smooth scroll with cubic-bezier ease-out. Used for
     * arrow nav and post-drag snap. Compositor still owns scrolling;
     * we just write `scrollLeft` each frame, so the browser can still
     * cancel us by starting a new wheel/touch gesture.
     *
     * @param {number} left Target scrollLeft (px).
     * @param {number} [duration]
     */
    _animateScrollTo(left, duration = SNAP_DURATION_MS) {
      cancelAnimationFrame(this._scrollAnimRaf);
      const startLeft = this._track.scrollLeft;
      const delta = left - startLeft;
      if (Math.abs(delta) < 0.5 || REDUCED_MOTION.matches) {
        this._track.scrollLeft = left;
        delete this.dataset.animating;
        // Run the conveyor recenter even on the no-animation path,
        // otherwise releases that land already grid-aligned (small
        // drag, perfect snap target) leave the DOM un-recentered.
        // After several such releases the buffer drains and the
        // infinite loop visibly stalls at one end of the rail.
        this._onScrollEnd();
        return;
      }
      // Disable native scroll-snap for the duration of the JS animation. With
      // scroll-snap-type: x mandatory active, the browser's snap engine fights
      // our per-frame scrollLeft writes: on desktop that shows as visible
      // jitter, and at high velocity it can yank the rail to an unrelated snap
      // point mid-animation (the "runaway loop" symptom). CSS keys off
      // data-animating to drop snap; we restore it on completion so the resting
      // position locks back to the grid.
      this.dataset.animating = "true";
      // Caller decides duration. For arrow nav this is a fixed
      // SNAP_DURATION_MS; for post-drag releases the caller derives
      // it from travel distance so the curve matches the user's flick.
      const t0 = performance.now();
      const tick = (now) => {
        const t = Math.min(1, (now - t0) / duration);
        this._track.scrollLeft = startLeft + delta * easeOutCubic(t);
        if (t < 1) {
          this._scrollAnimRaf = requestAnimationFrame(tick);
        } else {
          this._scrollAnimRaf = 0;
          // Clear the animating flag before running the conveyor recenter so
          // _onScrollEnd (called explicitly here, and via the natural scrollend
          // that follows) actually runs.
          delete this.dataset.animating;
          // Run the conveyor recenter synchronously now that we're at the
          // resting position. Otherwise the rebalance would wait for the
          // polyfill or native scrollend event, which we just suppressed during
          // the animation, leaving the rail un-recentered until the user's next
          // interaction kicks off scroll events.
          this._onScrollEnd();
        }
      };
      this._scrollAnimRaf = requestAnimationFrame(tick);
    }

    /**
     * Original (markup-order) position of the slide currently at the front of
     * the viewport. Used by the live region so screen-reader users hear
     * "Slide 2 of 8", referring to the content rather than the shuffled DOM
     * index.
     */
    _currentOriginalIndex() {
      const front =
        this._track.children[this._normalisedIndex()] ||
        this._track.children[0];
      if (!front) return 0;
      const idx = Number(front.dataset.freshOriginalIndex);
      return Number.isFinite(idx) ? idx : 0;
    }

    _normalisedIndex() {
      const n = this._track.children.length;
      if (!n) return 0;
      return ((this._index % n) + n) % n;
    }

    /**
     * Rotate the DOM so the active slide sits at the middle child, giving a
     * roughly equal buffer on both sides. The visual position is unchanged: we
     * adjust scrollLeft synchronously so the user never sees a jump.
     *
     * Called on init and after every scrollend, so a single arrow click or a
     * small flick never animates into a position with no rendered slide.
     */
    _recenter({ initial = false } = {}) {
      const total = this._track.children.length;
      if (total < 2 || !this._slideStride) return;

      // Rotating children would blow away keyboard focus inside a slide
      // (moving a focused element resets focus to <body>). Skip this
      // rebalance; the next scrollend after focus leaves will run it.
      if (
        !initial &&
        this._track.contains(document.activeElement) &&
        document.activeElement !== this._track
      ) {
        return;
      }

      const desired = Math.floor(total / 2);
      let shift = this._index - desired;
      if (shift === 0 && !initial) return;
      while (shift > 0) {
        this._track.appendChild(this._track.firstElementChild);
        this._index -= 1;
        shift -= 1;
      }
      while (shift < 0) {
        this._track.insertBefore(
          this._track.lastElementChild,
          this._track.firstElementChild,
        );
        this._index += 1;
        shift += 1;
      }
      this._slides = Array.from(this._track.children);
      // Snap scrollLeft to compensate for the DOM shift, without animation:
      // the visual position must not change.
      this._snapToIndex(this._index);
      this._syncActiveSlide();
    }

    _syncActiveSlide() {
      const nIdx = this._normalisedIndex();
      this._slides?.forEach((slide, i) => {
        if (i === nIdx) {
          slide.dataset.active = "true";
        } else {
          delete slide.dataset.active;
        }
      });
    }

    /**
     * Warm the browser's decode cache for every <img> in the track. We don't
     * change loading, so lazy images keep deferring their network request until
     * the browser deems them near-viewport. Once an image has finished its
     * fetch, we decode it ahead of time so the next paint that shows it doesn't
     * stall on synchronous decode.
     */
    _warmDecodeCache() {
      this._track.querySelectorAll("img").forEach((img) => {
        const decode = () => {
          if (typeof img.decode === "function") img.decode().catch(() => {});
        };
        if (img.complete && img.naturalWidth > 0) {
          decode();
        } else {
          img.addEventListener("load", decode, { once: true });
        }
      });
    }

    // Desktop click+drag-to-scroll

    /** @param {PointerEvent} e */
    _onPointerDown(e) {
      // Touch input: let the browser handle it natively. Native overflow scroll
      // on iOS runs on the compositor, which is what we're optimising for.
      if (e.pointerType === "touch") return;
      // Ignore right/middle click.
      if (e.button !== undefined && e.button !== 0) return;
      // Inert when there's nothing to scroll.
      if (this._disabled) return;
      // Don't start a drag from inside an arrow button; let the click handler
      // win.
      if (e.target.closest('[data-js^="carousel-arrow-"]')) return;

      this._isDragging = true;
      this._dragMoved = false;
      this._dragPointerId = e.pointerId;
      this._dragStartX = e.clientX;
      this._dragStartScrollLeft = this._track.scrollLeft;
      this._dragTargetScrollLeft = this._track.scrollLeft;
      this._dragVelocity = 0;
      this._dragLastX = e.clientX;
      this._dragLastT = e.timeStamp;
      // A fresh gesture cancels any in-flight snap animation so
      // the user is in control immediately.
      cancelAnimationFrame(this._scrollAnimRaf);
      this._scrollAnimRaf = 0;
    }

    /**
     * rAF loop running for the duration of a desktop drag. Reads
     * the latest target scrollLeft computed by `_onPointerMove` and
     * writes it once per frame.
     *
     * We batch because mouse pointermove events can fire faster than the
     * display refresh rate (1000Hz on gaming mice, 250Hz+ on regular ones).
     * Writing scrollLeft in the move handler forces a synchronous layout commit
     * and dispatches a scroll event per write, and that surplus work is wasted
     * because only the last write before vsync paints. Batching collapses N
     * writes per frame to 1 and is the single biggest fix for desktop drag
     * jank.
     */
    _dragRafTick = () => {
      if (!this._isDragging) {
        this._dragRaf = 0;
        return;
      }
      // Only write if changed, to save a scroll event roundtrip when the cursor
      // is stationary.
      if (this._track.scrollLeft !== this._dragTargetScrollLeft) {
        this._track.scrollLeft = this._dragTargetScrollLeft;
      }
      this._dragRaf = requestAnimationFrame(this._dragRafTick);
    };

    /** @param {PointerEvent} e */
    _onPointerMove(e) {
      if (!this._isDragging || e.pointerId !== this._dragPointerId) return;
      const dx = e.clientX - this._dragStartX;
      if (!this._dragMoved) {
        if (Math.abs(dx) < DRAG_THRESHOLD) return;
        this._dragMoved = true;
        // Pointer capture (next line) reassigns :hover to the captured track,
        // which drops the nested media-slider's :hover and collapses its
        // hover-image overlay back to the first image mid-drag. Pin the overlay
        // open on the grabbed card via a data attribute the overlay CSS also
        // honours; the hold is cleared on release in _onPointerUp. Queried
        // before setPointerCapture, while :hover is still valid.
        this._hoverHoldEl = this._track.querySelector(
          'media-slider.product-card__media:hover',
        );
        if (this._hoverHoldEl) this._hoverHoldEl.dataset.hoverHold = "true";
        try {
          this._track.setPointerCapture(e.pointerId);
        } catch {
          /* setPointerCapture can throw if the pointer is no longer active */
        }
        this.dataset.dragging = "true";
        // Start the rAF batcher only on the first qualifying move
        // so a tap-without-drag never schedules a frame.
        if (!this._dragRaf) {
          this._dragRaf = requestAnimationFrame(this._dragRafTick);
        }
      }
      // Compute the target scrollLeft cheaply, with no DOM writes here; the rAF
      // tick drains it once per frame.
      //
      // Clamp drag travel to MAX_RELEASE_SLIDES from the gesture start. Past
      // that we apply a soft rubberband so the rail keeps responding (no dead
      // wall) but at a fraction of cursor speed. Without the cap, dragging far
      // past the conveyor buffer edge meant release had to animate back many
      // slides to land within bounds, which showed as a fast reverse loop.
      const stride = this._slideStride || 1;
      const maxDrag = MAX_RELEASE_SLIDES * stride;
      let effectiveDx = dx;
      if (Math.abs(dx) > maxDrag) {
        const overflow = Math.abs(dx) - maxDrag;
        // Diminishing returns past the cap: each extra px of cursor
        // travel only moves the rail ~0.15px. Same shape iOS uses
        // for boundary rubberband.
        const rubber = overflow / (1 + overflow / (stride * 0.5));
        effectiveDx = (dx > 0 ? 1 : -1) * (maxDrag + rubber * 0.15);
      }
      this._dragTargetScrollLeft = this._dragStartScrollLeft - effectiveDx;

      // EMA-smoothed velocity in px/ms. Smoothing damps the
      // pause-before-release jitter that otherwise made flicks
      // misfire in the wrong direction.
      const dt = e.timeStamp - this._dragLastT;
      if (dt > 0) {
        const v = (e.clientX - this._dragLastX) / dt;
        this._dragVelocity = this._dragVelocity * 0.6 + v * 0.4;
      }
      this._dragLastX = e.clientX;
      this._dragLastT = e.timeStamp;
    }

    /** @param {PointerEvent} e */
    _onPointerUp(e) {
      if (!this._isDragging || e.pointerId !== this._dragPointerId) return;
      const moved = this._dragMoved;

      // Mark the host as animating BEFORE clearing data-dragging. Both
      // attributes drop scroll-snap via CSS, so snap stays suppressed across
      // the drag-to-release transition. Without the overlap, the brief window
      // between deleting data-dragging and starting the rAF animation lets the
      // browser's snap engine yank scrollLeft to an arbitrary snap point on our
      // final flush write, showing as a sudden jump.
      if (moved) this.dataset.animating = "true";

      this._isDragging = false;
      this._dragMoved = false;
      this._dragPointerId = null;
      delete this.dataset.dragging;
      // Release the hover-image hold set at drag start; if the cursor is still
      // over the card, its native :hover takes back over seamlessly.
      if (this._hoverHoldEl) {
        delete this._hoverHoldEl.dataset.hoverHold;
        this._hoverHoldEl = null;
      }
      // Stop the per-frame writer and flush one final write so the
      // released position matches the cursor exactly before the
      // snap animation takes over.
      cancelAnimationFrame(this._dragRaf);
      this._dragRaf = 0;
      if (moved && this._track.scrollLeft !== this._dragTargetScrollLeft) {
        this._track.scrollLeft = this._dragTargetScrollLeft;
      }

      if (!moved) {
        delete this.dataset.animating;
        return;
      }

      // Capture-phase click suppressor; reset on next tick in case
      // no click follows (e.g. pointercancel).
      this._suppressNextClick = true;
      setTimeout(() => {
        this._suppressNextClick = false;
      }, 0);

      if (!this._slideStride) {
        delete this.dataset.animating;
        return;
      }

      // Drag-distance + flick-bonus release model.
      //
      // Earlier revisions projected release momentum via friction kinematics
      // (s = v² / 2a) and clamped the projected rest position to a window
      // around startIdx. That had two failure modes that combined into the
      // "runaway loop":
      //
      //   1. After a hard rubberband-stretched drag, the rail's current
      //      position is already ~MAX_RELEASE_SLIDES from its start. A
      //      release-time velocity reversal then animated up to
      //      MAX_RELEASE_SLIDES backward from current, up to 2x the cap of true
      //      visible travel.
      //   2. A fast flick at the conveyor edge produced a huge velocity, hence
      //      a huge decelDistance and projected index, which the clamp honoured
      //      at its outer edge, animating the full distance even though the user
      //      only wanted one slide forward.
      //
      // The model below uses drag distance as the source of truth: how many
      // slides has the rail actually moved during the gesture? That's bounded
      // by the in-drag rubberband cap, so it can never exceed
      // MAX_RELEASE_SLIDES. Velocity only decides whether to add a one-slide
      // flick bonus in the gesture direction.
      const stride = this._slideStride;
      const startScroll = this._dragStartScrollLeft;
      const currentScroll = this._track.scrollLeft;
      const startIdx = Math.round(startScroll / stride);
      const draggedSlides = (currentScroll - startScroll) / stride;

      // Velocity in scroll direction (sign convention: +ve = forward, i.e.
      // scrollLeft increasing). _dragVelocity is finger-direction px/ms, so
      // negate.
      const v = -this._dragVelocity;
      const speed = Math.abs(v);
      const flickBonus = speed >= FLICK_VELOCITY ? Math.sign(v) : 0;

      // Snap to nearest grid slot crossed during the drag, then add
      // the optional flick bonus.
      let targetIdx = startIdx + Math.round(draggedSlides) + flickBonus;

      // Hard cap to ±MAX_RELEASE_SLIDES from start. The drag rubberband already
      // enforces this on draggedSlides, but the flick bonus can push it one
      // over, so cap explicitly.
      targetIdx = Math.max(
        startIdx - MAX_RELEASE_SLIDES,
        Math.min(startIdx + MAX_RELEASE_SLIDES, targetIdx),
      );

      // Duration paced by the actual slides traversed from the current position
      // (not the start), so a release that only needs to settle a half-slide
      // back to the grid finishes quickly rather than crawling through a 700ms
      // minimum.
      const targetScroll = targetIdx * stride;
      const remainingSlides = Math.abs(targetScroll - currentScroll) / stride;
      const duration = Math.max(
        RELEASE_DURATION_MIN,
        Math.min(
          RELEASE_DURATION_MAX,
          remainingSlides * RELEASE_MS_PER_SLIDE +
            // Velocity bonus tapers gracefully; capped so a
            // hyper-fast cursor flick can't blow the ceiling.
            Math.min(speed * 60, 200),
        ),
      );

      this._animateScrollTo(targetScroll, duration);
    }
  }

  /**
   * Drop focus left on a rail track after a drawer opened from within it closes.
   *
   * The track carries tabindex="0" for arrow-key scrolling. On Safari a tap on a
   * non-focusable child (e.g. a quick-add button) focuses the nearest focusable
   * ancestor — this track — so a dialog opened by that tap records the track as
   * its return target and, on close, Safari restores focus to it and paints the
   * keyboard :focus-visible ring around the whole rail. Genuine keyboard use
   * restores focus to the invoking control, never the track, so blurring here
   * clears the stray ring without touching keyboard navigation.
   * @returns {void}
   */
  function dropRestoredTrackFocus() {
    const active = document.activeElement;
    if (active && active.matches('[data-js="carousel-track"]')) active.blur();
  }
  document.addEventListener("drawer:closed", dropRestoredTrackFocus);

  window.customElements.define("carousel-slider", Carousel);
})();
